import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, ShieldCheck } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

export const AuditorDashboard = () => {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/governance/auditor/summary`, headers()).then((r) => setData(r.data)).catch(() => setData(null));
    axios.get(`${API}/admin/governance/audit?limit=50`, headers()).then((r) => setAudit(r.data.audit)).catch(() => setAudit([]));
  }, []);

  if (data === null) return null;
  const stats = [
    { label: "Total users", value: data.users_total },
    { label: "Suspended", value: data.suspended, warn: data.suspended > 0 },
    { label: "ID verified", value: data.verified_identities },
    { label: "Business verified", value: data.verified_businesses },
    { label: "Vendor compliant", value: data.compliance_approved },
    { label: "Open alerts", value: data.open_alerts, warn: data.open_alerts > 0 },
    { label: "Funding pending", value: data.funding_pending, warn: data.funding_pending > 0 },
    { label: "Pitches pending", value: data.pitches_pending, warn: data.pitches_pending > 0 },
    { label: "Audit entries", value: data.audit_entries },
  ];
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="auditor-dashboard">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><ShieldCheck className="h-5 w-5 text-[#F5B300]" /> Auditor Dashboard</p>
      <p className="mt-1 text-xs text-slate-500">Read-only compliance view — every action is logged and auditable (Rule 12).</p>
      <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="auditor-stats">
        {stats.map((s) => (
          <div key={s.label} className={`rounded-2xl border p-3.5 text-center ${s.warn ? "border-[#F97316]/30 bg-[#F97316]/[0.06]" : "border-white/10 bg-[#09090B]/60"}`}>
            <p className={`font-display text-xl font-extrabold ${s.warn ? "text-[#F97316]" : "text-white"}`}>{s.value}</p>
            <p className="mt-0.5 font-jbmono text-[9px] uppercase tracking-widest text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="mt-5">
        <p className="font-display text-sm font-bold">Audit trail (latest 50)</p>
        <div className="mt-2 max-h-72 space-y-1.5 overflow-y-auto">
          {(audit || []).map((a) => (
            <div key={a.id} data-testid={`audit-${a.id.slice(-6)}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#09090B]/80 px-3.5 py-2">
              <span className="shrink-0 font-jbmono text-[9px] uppercase tracking-wider text-[#06B6D4]">{a.action}</span>
              <span className="min-w-0 flex-1 truncate font-jbmono text-[10px] text-slate-400">{a.detail || a.target_type}</span>
              <span className="shrink-0 font-jbmono text-[9px] text-slate-600">{String(a.at).slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const EmergencyAccess = () => {
  const [grants, setGrants] = useState(null);
  const [form, setForm] = useState({ user_id: "", reason: "", minutes: 30 });
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/governance/emergency/active`, headers()).then((r) => setGrants(r.data.grants)).catch(() => setGrants([]));
  };
  useEffect(load, []);

  const grant = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/governance/emergency/grant`, form, headers());
      toast.success("Emergency access granted and logged.");
      setForm({ user_id: "", reason: "", minutes: 30 });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await axios.patch(`${API}/admin/governance/emergency/revoke/${id}`, {}, headers());
      toast.success("Access revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (grants === null) return null;
  const active = grants.filter((g) => g.active);
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="emergency-admin">
      <p className="font-display text-lg font-bold">🚨 Emergency Access ({active.length} active)</p>
      <p className="mt-1 text-xs text-slate-500">Time-boxed, fully audited access for incidents only (Rule 17).</p>
      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        <input data-testid="em-user" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} placeholder="User ID"
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
        <input data-testid="em-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason"
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
        <input data-testid="em-minutes" type="number" value={form.minutes} onChange={(e) => setForm({ ...form, minutes: parseInt(e.target.value, 10) || 30 })} placeholder="Minutes"
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
      </div>
      <button data-testid="em-grant" onClick={grant} disabled={busy || !form.user_id.trim() || !form.reason.trim()}
        className="mt-3 rounded-full bg-gradient-to-r from-[#EF4444] to-[#F97316] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
        {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : "Grant emergency access"}
      </button>
      {grants.length > 0 && (
        <div className="mt-4 space-y-2">
          {grants.map((g) => (
            <div key={g.created_at} data-testid={`em-row-${g.created_at.slice(0, 10)}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-3.5">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold">{g.email}</p>
                <p className="truncate font-jbmono text-[10px] text-slate-500">{g.reason} · expires {String(g.expires_at).slice(0, 16).replace("T", " ")}</p>
              </div>
              <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${g.active ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-white/5 text-slate-500"}`}>
                {g.active ? "active" : "expired"}
              </span>
              {g.active && (
                <button data-testid={`em-revoke-${g.created_at.slice(0, 10)}`} onClick={() => revoke(g._id || g.created_at)} className="rounded-full border border-[#EF4444]/40 px-3 py-1.5 text-[10px] font-bold text-[#EF4444]">
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
